import React, { useEffect, useState } from 'react';
import {
  AmenityReturn,
  ReservationReschedulePreviewReturn,
  ReservationReturn,
  ReservationRuleViolation,
} from '../clients/rust-api-client';
import { memberReservationService } from '../clients/ApiClients';
import { useImpersonating } from '../Hooks/useImpersonating';
import { Button } from '../Components/Catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '../Components/Catalyst/dialog';
import { Field, Label } from '../Components/Catalyst/fieldset';
import { Input } from '../Components/Catalyst/input';
import { Text } from '../Components/Catalyst/text';
import { ErrorAlert } from '../Components/Custom/ErrorAlert';
import LoadingSpinner from '../LoadingSpinner';
import {
  formatLocalRange,
  moveFeeOutcomeText,
  showPartySize,
  timesAreIn,
} from './reservationFormat';
import {
  rescheduleErrorMessage,
  reservationErrorMessage,
  ruleMessage,
  violationsFromError,
} from './ruleMessages';
import TimePicker, { PickedTime } from './TimePicker';

interface Props {
  reservation: ReservationReturn;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updated: ReservationReturn) => void;
}

type Step = 'pick' | 'review';

/**
 * Moving a booking to another time (#640 phase 2), allowed until the
 * cancellation deadline. The same picker as booking, with the booking
 * itself left out of the grid (`exclude_reservation_id`) so its own slot
 * reads as free.
 *
 * Review asks the reschedule preview, which judges the move exactly as
 * the move would — excluding the booking from its own overlap and limit
 * counts — and says what happens to the fee. A board member or manager
 * viewing as the household may run that preview (it writes nothing); only
 * confirming is closed to them.
 */
const RescheduleDialog: React.FC<Props> = ({ reservation: r, isOpen, onClose, onSuccess }) => {
  const [amenity, setAmenity] = useState<AmenityReturn | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>('pick');
  const [picked, setPicked] = useState<PickedTime | null>(null);
  const [partySize, setPartySize] = useState(String(r.party_size));
  const [preview, setPreview] = useState<ReservationReschedulePreviewReturn | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [violations, setViolations] = useState<ReservationRuleViolation[]>([]);
  const impersonating = useImpersonating();

  useEffect(() => {
    if (!isOpen) return;
    setStep('pick');
    setPicked(null);
    setPreview(null);
    setError(null);
    setViolations([]);
    setPartySize(String(r.party_size));
    let cancelled = false;
    memberReservationService
      .getMyAmenity({ homeId: r.home_id, amenityId: r.amenity_id })
      .then(({ data }) => {
        if (!cancelled) setAmenity(data);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(reservationErrorMessage(err, "Couldn't load this amenity."));
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, r.home_id, r.amenity_id, r.party_size]);

  const party = Math.max(1, parseInt(partySize, 10) || 1);
  const unchanged =
    picked?.start === r.start_local && picked?.end === r.end_local && party === r.party_size;

  const review = async (): Promise<void> => {
    if (!picked || !amenity) return;
    setError(null);
    setViolations([]);
    setWorking(true);
    try {
      const { data } = await memberReservationService.previewMyReservationReschedule({
        reservationId: r.id,
        memberReservationReschedule: {
          start_local: picked.start,
          end_local: picked.end,
          party_size: party,
        },
      });
      setPreview(data);
      setViolations(data.refused);
      setStep('review');
    } catch (err) {
      setError(rescheduleErrorMessage(err));
    } finally {
      setWorking(false);
    }
  };

  const confirm = async (): Promise<void> => {
    if (!picked || impersonating) return;
    setWorking(true);
    setError(null);
    setViolations([]);
    try {
      const { data } = await memberReservationService.rescheduleMyReservation({
        reservationId: r.id,
        memberReservationReschedule: {
          start_local: picked.start,
          end_local: picked.end,
          party_size: party,
        },
      });
      onSuccess(data);
    } catch (err) {
      const refused = violationsFromError(err);
      if (refused) setViolations(refused);
      else setError(rescheduleErrorMessage(err));
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} size="lg">
      <DialogTitle>Change the time</DialogTitle>
      <DialogDescription>
        {r.amenity_name} · now {formatLocalRange(r.start_local, r.end_local)}
      </DialogDescription>
      <DialogBody className="space-y-5">
        {loadError ? (
          <ErrorAlert>{loadError}</ErrorAlert>
        ) : !amenity ? (
          <LoadingSpinner />
        ) : (
          <>
            <Text className="text-sm">{timesAreIn(amenity.timezone)}</Text>
            {/* Kept mounted through review so going Back keeps the choice. */}
            <div className={step === 'pick' ? 'space-y-5' : 'hidden'}>
              <TimePicker
                amenity={amenity}
                homeId={r.home_id}
                initialDate={r.local_date}
                excludeReservationId={r.id}
                onChange={setPicked}
              />
              {picked && showPartySize(amenity) && (
                <Field>
                  <Label>How many people</Label>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={amenity.max_party_size ?? undefined}
                    value={partySize}
                    onChange={(e) => setPartySize(e.target.value)}
                  />
                </Field>
              )}
            </div>

            {step === 'review' && picked && (
              <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-[auto_1fr] sm:gap-x-6">
                <dt className="font-medium text-zinc-500 dark:text-zinc-400">From</dt>
                <dd className="text-zinc-950 dark:text-white">
                  {formatLocalRange(r.start_local, r.end_local)}
                </dd>
                <dt className="font-medium text-zinc-500 dark:text-zinc-400">To</dt>
                <dd className="text-zinc-950 dark:text-white">
                  {formatLocalRange(picked.start, picked.end)}
                </dd>
                {showPartySize(amenity) && party !== r.party_size && (
                  <>
                    <dt className="font-medium text-zinc-500 dark:text-zinc-400">People</dt>
                    <dd className="text-zinc-950 dark:text-white">
                      {r.party_size} → {party}
                    </dd>
                  </>
                )}
                {preview && (
                  <>
                    <dt className="font-medium text-zinc-500 dark:text-zinc-400">Fee</dt>
                    <dd className="text-zinc-950 dark:text-white">
                      {moveFeeOutcomeText(preview.fee_outcome, preview.fee_change)}
                    </dd>
                  </>
                )}
              </dl>
            )}
          </>
        )}

        {violations.length > 0 && (
          <ErrorAlert title="Can't move it to that time">
            <ul className="list-disc space-y-1 pl-4">
              {violations.map((v) => (
                <li key={v.code}>{ruleMessage(v.code, amenity)}</li>
              ))}
            </ul>
          </ErrorAlert>
        )}
        <ErrorAlert>{error}</ErrorAlert>
        {step === 'review' && impersonating && (
          <Text className="text-sm">
            This is a read-only view: you can see what the move would do, but only the household
            can confirm it.
          </Text>
        )}
      </DialogBody>
      <DialogActions>
        {step === 'review' ? (
          <>
            <Button plain onClick={() => setStep('pick')} disabled={working}>
              Back
            </Button>
            <Button
              color="sky"
              onClick={confirm}
              disabled={working || impersonating || !preview?.would_move}
            >
              {working ? 'Moving…' : 'Move booking'}
            </Button>
          </>
        ) : (
          <>
            <Button plain onClick={onClose} disabled={working}>
              Keep the current time
            </Button>
            <Button color="sky" onClick={review} disabled={!picked || unchanged || working}>
              {working ? 'Checking…' : 'Review'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RescheduleDialog;
